import { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Table, Button, Row, Col, Form } from 'react-bootstrap';
import Message from '../../Components/Message';
import Loader from '../../Components/Loader';
import { useGetOrdersQuery, useMarkOrdersSeenMutation } from '../../slices/ordersApiSlice';
import BackButton from "../../Components/BackButton";

const OrderListScreen = () => {
  const location = useLocation();
  const initialFilters = useMemo(() => location.state?.filters || {}, [location.state]);
  const customerFilter = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      userId: params.get('userId') || '',
      name: params.get('userName') || '',
      email: params.get('userEmail') || '',
    };
  }, [location.search]);

  // filters
  const [q, setQ] = useState(initialFilters.q || '');
  const [orderRef, setOrderRef] = useState(initialFilters.orderRef || '');
  const [invoice, setInvoice] = useState(initialFilters.invoice || '');
  const [quote, setQuote] = useState(initialFilters.quote || '');          
  const [dateFrom, setDateFrom] = useState(initialFilters.dateFrom || '');
  const [dateTo, setDateTo] = useState(initialFilters.dateTo || '');

  // pagination
  const [page, setPage] = useState(initialFilters.page || 1);
  const [pageSize, setPageSize] = useState(initialFilters.pageSize || 20);

  const { data, isLoading, error, refetch } = useGetOrdersQuery(
    {
      q,
      orderRef,
      invoice,
      quote,
      userId: customerFilter.userId,
      dateFrom,
      dateTo,
      page,
      pageSize,
    },
    { refetchOnMountOrArgChange: true }
  );

  const [markOrdersSeen, { isLoading: isMarking }] = useMarkOrdersSeenMutation();

  // normalize response
  const orders = Array.isArray(data) ? data : data?.orders || [];
  const total  = data?.total ?? orders.length;
  const pages  = data?.pages ?? 1;

  const currentFilters = {
    q,
    orderRef,
    invoice,
    quote,
    userId: customerFilter.userId,
    dateFrom,
    dateTo,
    page,
    pageSize,
  };

  const fromState = {
    pathname: location.pathname,
    search: location.search,
    state: { filters: currentFilters },
  };

  const submitHandler = (e) => {
    e.preventDefault();
    setPage(1);
    refetch();
  };

  const resetHandler = () => {
    setQ(''); setOrderRef(''); setInvoice(''); setQuote('');
    setDateFrom(''); setDateTo('');
    setPage(1);
    refetch();
  };

  // Manually mark all unseen orders as seen (called from button)
  const handleMarkAllSeen = async () => {
    try {
      await markOrdersSeen().unwrap();
      await refetch();
    } catch (err) {
      console.error('Failed to mark orders as seen', err);
    }
  };

  return (
    <>
    <BackButton />
      <Row className="align-items-center mb-3">
        <Col>
          <h1 className="mb-0">
            {customerFilter.userId
              ? `Orders for ${customerFilter.name || 'User'}`
              : 'Orders'}
          </h1>
          {customerFilter.userId && customerFilter.email && (
            <div className="text-muted">{customerFilter.email}</div>
          )}
        </Col>
        {customerFilter.userId && (
          <Col xs="auto" className="d-flex gap-2">
            <Button
              as={Link}
              to={`/admin/user/${customerFilter.userId}/edit`}
              variant="outline-secondary"
            >
              Back to User
            </Button>
            <Button as={Link} to="/admin/orderlist" variant="outline-primary">
              View All Orders
            </Button>
          </Col>
        )}
      </Row>

      {/* Filter Bar */}
      <Form className="mb-3" onSubmit={submitHandler}>
        <Row className="g-2 align-items-end">
          <Col md={3}>
            <Form.Label>Search name or email</Form.Label>
            <Form.Control
              placeholder="e.g., Jane or jane@kccparts.ca"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </Col>
          <Col md={2}>
            <Form.Label>Reference</Form.Label>
            <Form.Control
              placeholder="Order Ref"
              value={orderRef}
              onChange={(e) => setOrderRef(e.target.value)}
            />
          </Col>
          <Col md={2}>
            <Form.Label>Invoice #</Form.Label>
            <Form.Control
              placeholder="Invoice"
              value={invoice}
              onChange={(e) => setInvoice(e.target.value)}
            />
          </Col>
          <Col md={2}>
            <Form.Label>Quote #</Form.Label> {/* NEW */}
            <Form.Control
              placeholder="Quote"
              value={quote}
              onChange={(e) => setQuote(e.target.value)}
            />
          </Col>
          <Col md={1}>
            <Form.Label>From</Form.Label>
            <Form.Control
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </Col>
          <Col md={1}>
            <Form.Label>To</Form.Label>
            <Form.Control
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </Col>
        </Row>

        <Row className="g-2 mt-2">
          <Col md={2}>
            <Form.Label>Page size</Form.Label>
            <Form.Select
              value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </Form.Select>
          </Col>
           <Col className="d-flex align-items-end">
            <Button type="submit" variant="primary" className="me-2">
              Filter
            </Button>
            <Button
              type="button"
              variant="outline-secondary"
              onClick={resetHandler}
              className="me-2"
            >
              Reset
            </Button>
            <Button
              type="button"
              variant="outline-success"
              onClick={handleMarkAllSeen}
              disabled={isMarking}
            >
              {isMarking ? 'Marking…' : 'Mark all as seen'}
            </Button>
          </Col>
        </Row>
      </Form>

      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">
          {error?.data?.message || error.error || 'Failed to load orders'}
        </Message>
      ) : (
        <>
          <Table striped bordered hover responsive className="table-sm align-middle">
            <thead>
              <tr>
                <th>Invoice #</th>
                <th>Quote #</th>{/* NEW */}
                <th>Customer</th>
                <th>Reference</th>
                <th>Date</th>
                <th className="text-end">Total</th>
                <th>Order Status</th>
                <th>Seen</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => {
                const status = o?.status ||
                  (o?.isDelivered
                    ? 'Delivered'
                    : o?.isPaid
                      ? 'Paid'
                      : o?.paidAmount
                        ? 'Partially Paid'
                        : 'Unpaid');
                return (
                  <tr
                    key={o._id}
                    className={o.adminSeen ? '' : 'order-row-unseen'}
                  >
                    <td>{o.invoice || o.documentRefs?.invoiceNumber || '—'}</td>
                    <td>{o.documentRefs?.quoteNumber || o.quote?.number || '—'}</td>
                    <td>{o.user?.name || o.guest?.name || '—'}</td>
                    <td>{o.reference || o.orderRef || (o._id ? o._id.slice(-6).toUpperCase() : '—')}</td>
                    <td>{o.createdAt ? new Date(o.createdAt).toLocaleDateString() : '—'}</td>
                     <td className="text-end">${Number(o.totalPrice || 0).toFixed(2)}</td>
                    <td>
                      <span className="badge bg-info">{status}</span>
                    </td>
                    <td>
                      {o.adminSeen ? (
                        <span className="badge bg-success">Seen</span>
                      ) : (
                        <span className="badge bg-danger">Unseen</span>
                      )}
                    </td>
                    <td>
                      <Button
                        as={Link}
                        to={`/order/${o._id}`}
                        state={{ from: fromState }}
                        className="btn-sm"
                        variant="light"
                      >
                        Details
                      </Button>
                    </td>
                  </tr>
                );
              })}
              {orders.length === 0 && (
                <tr>
                  <td colSpan={9} className="text-center py-4">No orders found.</td>
                </tr>
              )}
            </tbody>
          </Table>

          {/* Footer: totals + pagination */}
          <div className="d-flex justify-content-between align-items-center">
            <div className="text-muted small">
              {(total ?? 0)} result{(total ?? 0) === 1 ? '' : 's'} • Page {page} / {pages ?? 1}
            </div>

            {pages > 1 && (
              <nav>
                <ul className="pagination mb-0">
                  <li className={`page-item ${page <= 1 ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => page > 1 && setPage(1)}>« First</button>
                  </li>
                  <li className={`page-item ${page <= 1 ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => page > 1 && setPage(page - 1)}>‹ Prev</button>
                  </li>
                  {Array.from({ length: pages }, (_, i) => i + 1).map((p) => (
                    <li key={p} className={`page-item ${p === page ? 'active' : ''}`}>
                      <button className="page-link" onClick={() => setPage(p)}>{p}</button>
                    </li>
                  ))}
                  <li className={`page-item ${page >= pages ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => page < pages && setPage(page + 1)}>Next ›</button>
                  </li>
                  <li className={`page-item ${page >= pages ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => page < pages && setPage(pages)}>Last »</button>
                  </li>
                </ul>
              </nav>
            )}
          </div>
        </>
      )}
    </>
  );
};

export default OrderListScreen;
